import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const EVENT_IDS = {
  START_ONBOARDING: '5998',
  PROFILE_BUILDER_NEXT: '3553',
  PROFILE_BUILDER_SAVE_AND_EXIT: '3554',
  ADDRESS_ENTRY_NEXT: '3555',
  ADDRESS_ENTRY_GO_BACK: '3556',
  ADDRESS_ENTRY_EXIT: '3557',
  ADDRESS_VERIFICATION_GO_BACK: '3558',
  ADDRESS_VERIFICATION_EXIT: '3559',
  CREATE_LISTING: '3560',
} as const;

// https://dataportal.zillowgroup.net/event-registry/5998
const StartOnboarding = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_ONBOARDING,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'zrm_user_onboarding',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3553
const ProfileBuilderNext = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROFILE_BUILDER_NEXT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'profile_builder',
      trigger_source_nm: 'next',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3554
const ProfileBuilderSaveAndExit = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROFILE_BUILDER_SAVE_AND_EXIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'profile_builder',
      trigger_source_nm: 'save_and_exit',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3555
const AddressEntryNext = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_ENTRY_NEXT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_entry',
      trigger_source_nm: 'next',
    },
    semantic: {
      semantic_event_nm: 'add_address',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3556
const AddressEntryGoBack = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_ENTRY_GO_BACK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_entry',
      trigger_source_nm: 'go_back',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3557
const AddressEntryExit = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_ENTRY_EXIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_entry',
      trigger_source_nm: 'exit',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3558
const AddressVerificationGoBack = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_VERIFICATION_GO_BACK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_verification',
      trigger_source_nm: 'go_back',
    },
    semantic: {
      semantic_event_nm: 'edit_address',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3559
const AddressVerificationExit = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_VERIFICATION_EXIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_verification',
      trigger_source_nm: 'exit',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3560
const CreateListing = ({ user, listing }: { user: ZrmClickstreamUser; listing: ZrmClickstreamListing }) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CREATE_LISTING,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_verification',
      trigger_source_nm: 'confirm',
    },
    semantic: {
      semantic_event_nm: 'create_listing',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

const NewUserOnboardingCSEvents = {
  AddressEntryExit,
  AddressEntryGoBack,
  AddressEntryNext,
  AddressVerificationExit,
  AddressVerificationGoBack,
  CreateListing,
  ProfileBuilderNext,
  ProfileBuilderSaveAndExit,
  StartOnboarding,
};

// Clickstream visualizer data-eventid attributes
StartOnboarding.data_event_id = EVENT_IDS.START_ONBOARDING;
ProfileBuilderNext.data_event_id = EVENT_IDS.PROFILE_BUILDER_NEXT;
ProfileBuilderSaveAndExit.data_event_id = EVENT_IDS.PROFILE_BUILDER_SAVE_AND_EXIT;
AddressEntryNext.data_event_id = EVENT_IDS.ADDRESS_ENTRY_NEXT;
AddressEntryGoBack.data_event_id = EVENT_IDS.ADDRESS_ENTRY_GO_BACK;
AddressEntryExit.data_event_id = EVENT_IDS.ADDRESS_ENTRY_EXIT;
AddressVerificationGoBack.data_event_id = EVENT_IDS.ADDRESS_VERIFICATION_GO_BACK;
AddressVerificationExit.data_event_id = EVENT_IDS.ADDRESS_VERIFICATION_EXIT;
CreateListing.data_event_id = EVENT_IDS.CREATE_LISTING;

export default NewUserOnboardingCSEvents;
